
if (!customElements.get('delivery-time')) {
  customElements.define(
    'delivery-time',
    class DeliveryTime extends HTMLElement {

      connectedCallback() {
        this.mode = this.dataset.deliveryMode || 'text';

        this.content = this.querySelector(
          '.wt-product__delivery-time_content'
        );

        if (!this.content) return;

        // Find the Shopify line-item property input.
        this.propertyInput = this.querySelector(
          '[data-delivery-property]'
        );

        // Initialize the property with the delivery time
        // rendered by Liquid for the initially selected variant.
        this.setPropertyValue(this.content.textContent);

        // Static delivery dates do not change with variants.
        // The line-item property has already been initialized.
        if (
          this.mode === 'days' &&
          this.dataset.static === 'true'
        ) {
          return;
        }

        // Read delivery information for all variants.
        const dataElement = this.querySelector(
          this.mode === 'days'
            ? '[data-delivery-days]'
            : '[data-delivery-times]'
        );

        if (!dataElement) return;

        try {
          this.variantsData = JSON.parse(dataElement.textContent);
        } catch (error) {
          console.error('Could not parse delivery data:', error);
          return;
        }

        // Find the Shopify section containing this component.
        this.section = document.getElementById(
          this.dataset.sectionId
        );

        if (!this.section) return;

        // German Roble theme uses Shopify's pub/sub system.
        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          (event) => {
            const data = event.data;
            if (!data) return;

            // Normalize the section ID.
            const sectionId = String(data.sectionId);

            const fullSectionId = sectionId.startsWith(
              'shopify-section-'
            )
              ? sectionId
              : 'shopify-section-' + sectionId;

            // Ignore events from other product sections.
            if (fullSectionId !== this.dataset.sectionId) {
              return;
            }

            // Get the selected variant from the event.
            const variant = data.variant;
            if (!variant) return;

            this.updateDeliveryTime(variant.id);
          }
        );
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
          this.variantChangeUnsubscriber = null;
        }

        clearTimeout(this.fadeTimer);
      }

      updateDeliveryTime(variantId) {
        const key = String(variantId);

        // Ignore events for variants belonging
        // to a different product.
        if (!Object.prototype.hasOwnProperty.call(
          this.variantsData, key
        )) {
          return;
        }

        const value = this.variantsData[key];
        let newText = '';

        if (this.mode === 'days') {
          newText = this.calculateDeliveryDates(value);
        } else {
          newText = value || '';
        }

        // Update Shopify line-item property immediately.
        // This happens before the fade animation completes,
        // so Add to Cart uses the latest value.
        this.setPropertyValue(newText);

        // Update visible text with fade animation.
        this.updateWithFade(newText);
      }

      // Set the hidden Shopify line-item property.
      setPropertyValue(value) {
        if (!this.propertyInput) return;

        this.propertyInput.value = String(value ?? '')
          .replace(/\s+/g, ' ')
          .trim();
      }

      calculateDeliveryDates(expectedDays) {
        if (
          expectedDays === null ||
          expectedDays === undefined ||
          expectedDays === ''
        ) {
          return '';
        }

        const days = Number(expectedDays);
        const range = Number(this.dataset.rangeDays || 0);

        if (
          !Number.isFinite(days) ||
          !Number.isFinite(range)
        ) {
          return '';
        }

        // Same calculation as Shopify Liquid.
        // Minimum start date: tomorrow.
        const startOffset = Math.max(1, days - range);

        const endOffset = Math.max(
          startOffset,
          days + range + 1
        );

        // Use the store's calendar date supplied by Liquid.
        // UTC arithmetic prevents browser timezone and DST
        // differences from changing the calendar day.
        const [year, month, day] = this.dataset.today
          .split('-')
          .map(Number);

        const baseDate = Date.UTC(year, month - 1, day);

        const startDate = new Date(
          baseDate + startOffset * 86400000
        );

        const endDate = new Date(
          baseDate + endOffset * 86400000
        );

        const startDay = startDate.getUTCDate();
        const endDay = endDate.getUTCDate();

        // German month names.
        const monthFormatter = new Intl.DateTimeFormat(
          this.dataset.locale || 'de-DE',
          {
            month: 'long',
            timeZone: 'UTC'
          }
        );

        const startMonth = monthFormatter.format(startDate);
        const endMonth = monthFormatter.format(endDate);

        const sameMonth =
          startDate.getUTCFullYear() ===
            endDate.getUTCFullYear() &&
          startDate.getUTCMonth() ===
            endDate.getUTCMonth();

        const sameDay =
          sameMonth && startDay === endDay;

        // German delivery date formatting.
        if (sameDay) {
          return `Lieferung am ${startDay}. ${startMonth}.`;
        }

        if (sameMonth) {
          return `Lieferung zwischen dem ${startDay}. und dem ${endDay}. ${startMonth}.`;
        }

        return `Lieferung zwischen dem ${startDay}. ${startMonth} und dem ${endDay}. ${endMonth}.`;
      }

      updateWithFade(newText) {
        clearTimeout(this.fadeTimer);

        const currentText = this.content.textContent
          .replace(/\s+/g, ' ')
          .trim();

        if (currentText === newText) {
          this.classList.remove('fade-out');
          return;
        }

        this.classList.add('fade-out');

        this.fadeTimer = setTimeout(() => {
          this.content.textContent = newText;
          this.classList.remove('fade-out');
        }, 300);
      }
    }
  );
}
